import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import '@fortawesome/fontawesome-free/css/all.min.css';

/**
 * Font Awesome tokens in bot text — `<<fas fa-home>> Main menu` — drawn as
 * icons in Validate's bubbles and option chips, as the widget draws them.
 *
 * The rules are Pype's (`packages/apps/pype/src/common/utils/icon-tokens.ts`):
 * an `indexOf` scan, never a regex over the bot's string; a token must mention
 * `fa-` and name a prefix and an icon; `< <` (a space authoring tools insert)
 * counts as `<<`; anything malformed stays visible text. The artwork is Font
 * Awesome Free, the same pinned package Pype ships, as its webfont: its CSS
 * keeps the v5 names (`fa-house-damage`, `fa-tasks`) real solutions use.
 *
 * One difference from Pype, on purpose: Pype draws nothing for Font Awesome
 * Pro prefixes (`fad`, `fal`, `fat`), which it can't ship. Solutions written for
 * the legacy widget use them, so here they fall back to the solid style rather
 * than vanishing, and the designer still sees which icon the reply names.
 */

const OPEN = '<<';
const CLOSE = '>>';

const NAME = /^[a-z0-9-]{1,60}$/;

/** Token prefix → Font Awesome Free style class. Pro styles draw as solid. */
const STYLE: Readonly<Record<string, string>> = {
  fas: 'fa-solid',
  'fa-solid': 'fa-solid',
  far: 'fa-regular',
  'fa-regular': 'fa-regular',
  fab: 'fa-brands',
  'fa-brands': 'fa-brands',
  fad: 'fa-solid',
  'fa-duotone': 'fa-solid',
  fal: 'fa-solid',
  'fa-light': 'fa-solid',
  fat: 'fa-solid',
  'fa-thin': 'fa-solid',
};

export interface IconToken {
  /** The text the token took up. */
  raw: string;
  /** Null when malformed: `raw` then shows as text. */
  icon: { style: string | null; name: string } | null;
}

const normalize = (text: string) =>
  text.includes('fa-') && text.includes('< <')
    ? text.replaceAll('< <', OPEN)
    : text;

/** The token starting at `start`, or null when there's no `<<` there. */
export const matchIconToken = (src: string, start = 0): IconToken | null => {
  if (!src.startsWith(OPEN, start)) return null;
  const close = src.indexOf(CLOSE, start + OPEN.length);
  if (close === -1) return { raw: OPEN, icon: null };
  const raw = src.slice(start, close + CLOSE.length);
  const inner = src.slice(start + OPEN.length, close).trim();
  if (!inner.includes('fa-')) return { raw, icon: null };
  const [prefix, token] = inner.split(/\s+/);
  if (!prefix || !token) return { raw, icon: null };
  const name = token.startsWith('fa-') ? token.slice(3) : token;
  return {
    raw,
    // A well-formed token whose style or name can't be drawn is still a
    // token: it's dropped from the text, as in the widget, not shown as markup.
    icon: { style: NAME.test(name) ? (STYLE[prefix] ?? null) : null, name },
  };
};

/** Split text into plain strings and tokens, in order. */
export const iconParts = (text: string): (string | IconToken)[] => {
  const src = normalize(text);
  const parts: (string | IconToken)[] = [];
  let i = 0;
  while (i < src.length) {
    const at = src.indexOf(OPEN, i);
    if (at === -1) {
      parts.push(src.slice(i));
      break;
    }
    if (at > i) parts.push(src.slice(i, at));
    const token = matchIconToken(src, at) as IconToken;
    parts.push(token);
    i = at + token.raw.length;
  }
  return parts;
};

/** The text a screen reader should hear: tokens removed, spaces collapsed. */
export const withoutIcons = (text: string): string =>
  iconParts(text)
    .map((p) => (typeof p === 'string' ? p : p.icon ? '' : p.raw))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * One icon. A name the token allows but Font Awesome Free doesn't have (most
 * often a Pro-only icon) has no glyph, and its box would still hold a blank
 * gap before the label, so it hides itself once drawn.
 */
const FaIcon = ({ className }: { className: string }) => {
  const ref = useRef<HTMLElement>(null);
  const [blank, setBlank] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const glyph = window.getComputedStyle(el, '::before').content;
    setBlank(
      !glyph || glyph === 'none' || glyph === 'normal' || glyph === '""'
    );
  }, [className]);
  return blank ? null : (
    <i ref={ref} className={className} aria-hidden="true" />
  );
};

/** Text with its icon tokens drawn as Font Awesome icons. */
export const renderIcons = (text: string, keyBase = 'i'): ReactNode[] => {
  if (!text.includes('<')) return [text];
  return iconParts(text).flatMap((p, k): ReactNode[] => {
    if (typeof p === 'string') return [p];
    if (!p.icon) return [p.raw];
    if (!p.icon.style) return [];
    return [
      <FaIcon
        key={`${keyBase}-${k}`}
        className={`${p.icon.style} fa-${p.icon.name} c-validate-fa`}
      />,
    ];
  });
};
